import { useContext, useEffect, useRef, useState } from "react";
import { Button, Card } from "react-bootstrap";
import {
  LuBuilding2,
  LuCheck,
  LuEye,
  LuPencil,
  LuRotateCcw,
  LuSave,
  LuShield,
} from "react-icons/lu";

import { User, UserOrg } from "@/models/user.tsx";
import {
  useOrgPermissions,
  RECORD_TYPE_LABELS,
  ADDITIONAL_PERMISSION_LABELS,
} from "../hooks/useOrgPermissions";
import { CoreDataModel } from "@/models";
import LoaderButton from "@/components/shared/LoaderButton";
import { FileSubmissionsContext } from "@/components/layout/FileSubmissionsTracker";

const ORG_TYPE_STATE = 2;

export function UserOrgPermissionsCard({
  user,
  org,
  isAdmin,
  leaid,
  coreData,
  onDirtyChange,
}: {
  user: User<UserOrg>;
  org: UserOrg;
  isAdmin: boolean;
  leaid: string;
  coreData: CoreDataModel;
  onDirtyChange?: (orgId: number, dirty: boolean) => void;
}) {
  const { showToast } = useContext(FileSubmissionsContext);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  const perms = useOrgPermissions(org, coreData, leaid, user, () => {
    showToast(
      "success",
      "Success",
      `Permissions for ${org.orgName ?? "organization"} saved successfully.`
    );
  });

  useEffect(() => {
    if (org.userOrgId !== undefined) {
      onDirtyChange?.(org.userOrgId, perms.isDirty);
    }
  }, [perms.isDirty, org.userOrgId, onDirtyChange]);

  const { recordTypes, additionalPermissions } = perms;

  const allView = recordTypes.every((rt) => perms.viewPerms[rt]);
  const allEdit = recordTypes.every((rt) => perms.editPerms[rt]);
  const allAdditional =
    additionalPermissions.length > 0 &&
    additionalPermissions.every((p) => perms.additionalPerms[p]);
  const allFullAccess =
    recordTypes.length > 0 &&
    allView &&
    allEdit &&
    (additionalPermissions.length === 0 || allAdditional);

  const bulkBtnStyle = (active: boolean): React.CSSProperties => ({
    fontSize: "0.8rem",
    borderRadius: 6,
    padding: "5px 12px",
    border: `1px solid ${active ? "var(--color-p2)" : "#cbd5e1"}`,
    background: active ? "rgba(0,58,93,0.08)" : "#f8fafc",
    color: active ? "var(--color-p2)" : "#64748b",
    fontWeight: active ? 600 : 500,
    transition: "all 0.15s",
  });

  const getDomainLabel = (rt: string) => {
    const label = RECORD_TYPE_LABELS[rt];
    return label ? `${label} (${rt})` : rt;
  };

  const getPermissionLabel = (p: string) =>
    ADDITIONAL_PERMISSION_LABELS[p] ?? p;

  return (
    <>
      <Card className="no-padding card-border overflow-hidden shadow">
        <div
          className="px-4 py-3 d-flex align-items-center gap-2 fw-semibold"
          style={{
            background: "var(--color-p3)",
            color: "#fff",
            fontSize: "0.975rem",
          }}
        >
          <LuBuilding2 size={16} style={{ flexShrink: 0 }} />
          {org.orgName ?? "Organization"}
        </div>

        <div className="p-4">
          {perms.adminRole && (
            <div
              className="d-flex align-items-center gap-3 p-3 mb-4"
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: 8,
                background: perms.isOrgAdmin ? "rgba(0,58,93,0.03)" : "#fff",
                cursor: isAdmin ? "pointer" : "default",
              }}
            >
              <input
                type="checkbox"
                checked={perms.isOrgAdmin}
                disabled={!isAdmin}
                onChange={() => perms.setIsOrgAdmin((v) => !v)}
                style={{
                  width: 15,
                  height: 15,
                  accentColor: "var(--color-p2)",
                  flexShrink: 0,
                }}
              />
              <LuShield
                size={16}
                style={{
                  color: perms.isOrgAdmin ? "var(--color-p2)" : "#94a3b8",
                  flexShrink: 0,
                }}
              />
              <div>
                <div
                  className="fw-semibold"
                  style={{
                    fontSize: "0.9rem",
                    color: perms.isOrgAdmin ? "var(--color-p2)" : "#1e293b",
                  }}
                >
                  Administrator
                </div>
                <div className="text-muted" style={{ fontSize: "0.8rem" }}>
                  {perms.isOrgAdmin
                    ? "Full administrative privileges enabled"
                    : "Grant full admin access"}
                </div>
              </div>
            </div>
          )}

          {recordTypes.length > 0 && (
            <>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <span
                  className="fw-semibold"
                  style={{
                    fontSize: "0.85rem",
                    letterSpacing: "0.07em",
                    textTransform: "uppercase",
                    color: "#475569",
                  }}
                >
                  Data Access
                </span>
                {isAdmin && (
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-sm d-flex align-items-center gap-1"
                      style={bulkBtnStyle(allFullAccess)}
                      onClick={perms.setFullAccess}
                    >
                      <LuCheck size={11} /> Full Access
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm d-flex align-items-center gap-1"
                      style={bulkBtnStyle(allEdit)}
                      onClick={perms.setAllEdit}
                    >
                      <LuPencil size={11} /> All Edit
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm d-flex align-items-center gap-1"
                      style={bulkBtnStyle(allView)}
                      onClick={perms.setAllView}
                    >
                      <LuEye size={11} /> All View
                    </button>
                    {additionalPermissions.length > 0 && (
                      <button
                        type="button"
                        className="btn btn-sm d-flex align-items-center gap-1"
                        style={bulkBtnStyle(allAdditional)}
                        onClick={perms.setAllAdditional}
                      >
                        <LuCheck size={11} /> All Additional
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="row g-2 mb-4">
                {recordTypes.map((rt) => (
                  <div key={rt} className="col-12 col-md-6">
                    <div
                      className="d-flex align-items-center justify-content-between px-3"
                      style={{
                        border: "1px solid #e2e8f0",
                        borderRadius: 8,
                        background: "#fff",
                        height: 40,
                      }}
                    >
                      <span
                        className="fw-semibold"
                        style={{
                          fontSize: "0.85rem",
                          color: "#1e293b",
                        }}
                      >
                        {getDomainLabel(rt)}
                      </span>
                      <div className="d-flex gap-2 align-items-center">
                        {(["view", "edit"] as const).map((type) => {
                          const active =
                            type === "view"
                              ? perms.viewPerms[rt]
                              : perms.editPerms[rt];
                          return (
                            <label
                              key={type}
                              className="d-flex align-items-center gap-1 mb-0"
                              style={{
                                fontSize: "0.82rem",
                                color: active ? "var(--color-p2)" : "#64748b",
                                cursor: isAdmin ? "pointer" : "default",
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={active}
                                disabled={!isAdmin}
                                onChange={() => perms.toggle(type, rt)}
                                style={{
                                  accentColor: "var(--color-p2)",
                                  width: 15,
                                  height: 15,
                                }}
                              />
                              {type.charAt(0).toUpperCase() + type.slice(1)}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {additionalPermissions.length > 0 && (
            <>
              <div
                className="fw-semibold mb-2"
                style={{
                  fontSize: "0.85rem",
                  letterSpacing: "0.07em",
                  textTransform: "uppercase",
                  color: "#475569",
                }}
              >
                Additional Permissions
              </div>
              <div className="d-flex flex-wrap gap-2">
                {additionalPermissions.map((p) => {
                  const active = perms.additionalPerms[p];
                  return (
                    <button
                      key={p}
                      type="button"
                      className="btn d-flex align-items-center gap-2"
                      style={{
                        fontSize: "0.85rem",
                        fontWeight: active ? 600 : 400,
                        borderRadius: 999,
                        padding: "6px 20px",
                        border: `1.5px solid ${active ? "var(--color-p2)" : "#94a3b8"}`,
                        background: active ? "rgba(0,58,93,0.08)" : "#fff",
                        color: active ? "var(--color-p2)" : "#475569",
                        cursor: isAdmin ? "pointer" : "default",
                        transition: "all 0.15s",
                        lineHeight: 1.4,
                      }}
                      onClick={() => {
                        if (!isAdmin) return;
                        perms.toggleAdditionalPerm(p);
                      }}
                      disabled={!isAdmin}
                    >
                      {active && <LuCheck size={13} />}
                      {getPermissionLabel(p)}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {isAdmin && (
          <div
            className="d-flex align-items-center justify-content-between px-4 py-3"
            style={{ borderTop: "1px solid #e5e7eb" }}
          >
            <div>
              {org.orgType !== ORG_TYPE_STATE && (
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  onClick={() => setShowRemoveConfirm(true)}
                >
                  Remove Access
                </Button>
              )}
            </div>

            <div className="d-flex align-items-center gap-3">
              <span
                style={{
                  fontSize: "0.9rem",
                  color: perms.isDirty ? "#ef4444" : "#94a3b8",
                  fontWeight: perms.isDirty ? 500 : 400,
                }}
              >
                {perms.isDirty
                  ? "You have unsaved permission changes"
                  : "No changes made"}
              </span>
              {perms.isDirty && (
                <Button
                  type="button"
                  variant="outline-secondary"
                  size="sm"
                  className="d-flex align-items-center gap-1"
                  onClick={perms.handleReset}
                >
                  <LuRotateCcw size={13} />
                  Reset
                </Button>
              )}
              <LoaderButton
                type="button"
                size="sm"
                loading={perms.mutationState.isPending}
                loadingText="Saving..."
                className="d-flex align-items-center gap-2"
                disabled={!perms.isDirty}
                style={{
                  background: perms.isDirty ? "var(--color-p3)" : "#e9ecef",
                  border: "none",
                  color: perms.isDirty ? "#fff" : "#6c757d",
                }}
                onClick={perms.handleSave}
              >
                <LuSave size={14} />
                Save Permissions
              </LoaderButton>
            </div>
          </div>
        )}
      </Card>

      {showRemoveConfirm && (
        <RemoveAccessConfirmModal
          orgName={org.orgName ?? "this organization"}
          onCancel={() => setShowRemoveConfirm(false)}
          onConfirm={() => {
            setShowRemoveConfirm(false);
            perms.handleDelete();
          }}
        />
      )}
    </>
  );
}

function RemoveAccessConfirmModal({
  orgName,
  onCancel,
  onConfirm,
}: {
  orgName: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const triggerElement = document.activeElement as HTMLElement;
    cancelRef.current?.focus();
    return () => {
      document.body.style.overflow = originalOverflow;
      triggerElement?.focus();
    };
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      onCancel();
      return;
    }
    if (e.key === "Tab") {
      if (e.shiftKey && document.activeElement === cancelRef.current) {
        e.preventDefault();
        confirmRef.current?.focus();
      } else if (!e.shiftKey && document.activeElement === confirmRef.current) {
        e.preventDefault();
        cancelRef.current?.focus();
      }
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="remove-access-title"
      aria-describedby="remove-access-desc"
      onKeyDown={handleKeyDown}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
    >
      <div
        style={{
          background: "white",
          padding: "32px 40px",
          borderRadius: "8px",
          maxWidth: "480px",
          width: "90%",
          boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
        }}
      >
        <h5
          id="remove-access-title"
          style={{
            fontSize: "16px",
            fontWeight: "600",
            color: "#333",
            margin: "0 0 12px 0",
          }}
        >
          Remove Access to {orgName}?
        </h5>
        <p
          id="remove-access-desc"
          style={{
            fontSize: "14px",
            color: "#666",
            lineHeight: "1.5",
            margin: "0 0 24px 0",
          }}
        >
          This will remove all permissions for this user to{" "}
          <strong>{orgName}</strong>. This action cannot be undone.
        </p>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "12px",
          }}
        >
          <button
            ref={cancelRef}
            onClick={onCancel}
            style={{
              padding: "8px 20px",
              background: "white",
              border: "1px solid #ddd",
              borderRadius: "4px",
              color: "#333",
              fontSize: "14px",
              cursor: "pointer",
              fontWeight: "500",
            }}
          >
            Cancel
          </button>
          <button
            ref={confirmRef}
            onClick={onConfirm}
            style={{
              padding: "8px 20px",
              background: "#dc3545",
              border: "none",
              borderRadius: "4px",
              color: "white",
              fontSize: "14px",
              cursor: "pointer",
              fontWeight: "500",
            }}
          >
            Remove Access
          </button>
        </div>
      </div>
    </div>
  );
}
